import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useQueries } from '@tanstack/react-query';
import { Printer } from 'lucide-react';
import { createQuoteRequest, getStockItemComponents } from '@/api/stock';
import { ApiError } from '@/api/client';
import { AlertDialog } from '@/components/ui/AlertDialog';
import type { StockComponent, StockItem } from '@/types/oscar';
import { supplierFields } from './supplierFields';

/** DD/MM/YYYY, per the house convention. */
function today(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

export interface QuoteLine {
  item: StockItem;
  qty: number | '';
}

// ───────────────────────────── the dialog ──────────────────────────────────

/**
 * Choose quantities and who it is for, before printing.
 *
 * <p>Anthony, 13/10/2026: <i>"I need to be able to select items from the stock
 * list so I can get them quoted... multiselect and goes to a pdf that can be
 * downloaded and include the information in the stock item - except
 * price."</i></p>
 *
 * ⚠ NOTHING HERE IS STORED. The quantity, the supplier and the reference
 * belong to ONE document, not to the stock line — a line does not have "a
 * quantity we are asking for", it has a count on a shelf.
 */
export function QuoteRequestDialog({ items, onClose, onReady }: {
  items: StockItem[];
  onClose: () => void;
  onReady: (lines: QuoteLine[], supplier: string, reference: string) => void;
}) {
  const [lines, setLines] = useState<QuoteLine[]>(() => items.map((item) => ({ item, qty: 1 })));
  const [supplier, setSupplier] = useState('');
  const [reference, setReference] = useState('');
  const [error, setError] = useState<string | null>(null);

  // ⛔ THE REQUEST IS KEPT BEFORE IT IS PRINTED (107). A printed request
  //    nobody stored is one no answer can come back to, which was the whole
  //    gap !1794 left. The spec is NOT sent - the server snapshots it from the
  //    stock lines, so nothing here can record a specification that was never
  //    shown.
  const save = useMutation({
    mutationFn: () => createQuoteRequest({
      Reference: reference.trim() || null,
      Lines: lines.map((l) => ({
        StockItemId: l.item.StockItemId,
        Qty: l.qty === '' ? 1 : l.qty,
      })),
    }),
    onSuccess: () => onReady(lines, supplier.trim(), reference.trim()),
    onError: (err) => setError(
      err instanceof ApiError
        ? `The request could not be saved, so it has not been printed: ${err.message}`
        : 'The request could not be saved, so it has not been printed.',
    ),
  });

  const setQty = (id: number, qty: number | '') =>
    setLines(lines.map((l) => (l.item.StockItemId === id ? { ...l, qty } : l)));

  const cls = 'mt-1 w-full rounded border border-border bg-white px-2 py-1.5 text-sm font-normal';

  return (
    <AlertDialog
      open
      wide
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={`Request a quote — ${items.length} item${items.length === 1 ? '' : 's'}`}
      confirmLabel="Save and preview"
      confirmDisabled={save.isPending}
      closeOnConfirm={false}
      onConfirm={() => { setError(null); save.mutate(); }}
    >
      <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1">
        {error && (
          <div role="alert" className="rounded border border-red/30 bg-red/10 px-2 py-1 text-xs text-red">
            {error}
          </div>
        )}

        <p className="text-xs font-normal text-darkGrey/70">
          The document lists what each item is, its part number and what it is made of. It does
          not carry what we paid, our internal notes, or how many we hold. The request is kept,
          so quotes can be recorded against it on the <strong>Quote requests</strong> screen.
        </p>

        <div className="grid gap-2 sm:grid-cols-2">
          <label className="block text-xs font-semibold text-darkGrey">Supplier (optional)
            <input value={supplier} onChange={(e) => setSupplier(e.target.value)} className={cls} />
          </label>
          <label className="block text-xs font-semibold text-darkGrey">Our reference (optional)
            <input value={reference} onChange={(e) => setReference(e.target.value)} className={cls} />
          </label>
        </div>

        <div className="space-y-2">
          {lines.map((l, i) => (
            <div key={l.item.StockItemId} className="flex items-start gap-2 rounded border border-border bg-bg/40 px-2 py-2">
              <label className="w-20 shrink-0 text-xs font-semibold text-darkGrey">
                How many
                <input
                  type="number"
                  min={1}
                  aria-label={`Quantity for ${l.item.Name}`}
                  value={l.qty}
                  /* ⛔ EMPTY STAYS EMPTY while typing — coercing here is what
                     made the parts editor turn "2" into 12. */
                  onChange={(e) => {
                    const v = e.target.value;
                    setQty(l.item.StockItemId, v === '' ? '' : (Number(v) >= 1 ? Number(v) : 1));
                  }}
                  className={cls}
                />
              </label>
              <div className="min-w-0 flex-1 pt-5">
                <p className="truncate text-sm font-semibold text-navy">{l.item.Name}</p>
                {/* ⚠ Says when a line has nothing a supplier could price, while
                    there is still time to go and describe it. */}
                {!l.item.Description && !l.item.SupplierPartNumber && (
                  <p className="mt-0.5 text-xs font-normal text-orange">
                    No description or part number — a supplier cannot tell what this is.
                    Add one with Edit before sending.
                  </p>
                )}
              </div>
              <span className="sr-only">{`Item ${i + 1}`}</span>
            </div>
          ))}
        </div>
      </div>
    </AlertDialog>
  );
}

// ────────────────────────── the printable document ─────────────────────────

/**
 * The quote request, laid out for A4.
 *
 * ⛔ PRINTED, NOT GENERATED. The browser's "Save as PDF" makes the file, which
 * is the pattern fintrack/CeaPrint and cab/CyberIncidentReport already use —
 * and it keeps a PDF library out of the repo for a page that is a page.
 *
 * ⚠ Nothing is sent anywhere. The document is produced on this machine and the
 * person decides who receives it, which is the right shape for anything going
 * outside Onsite.
 */
export function QuoteRequestPrint({ lines, supplier, reference, onBack }: {
  lines: QuoteLine[];
  supplier: string;
  reference: string;
  onBack: () => void;
}) {
  // ⚠ One read per item rather than a bulk endpoint — a handful of lines, and
  //   adding an endpoint would change the API surface for a page that can ask
  //   for what it needs with what already exists.
  const partQueries = useQueries({
    queries: lines.map((l) => ({
      queryKey: ['stock-item-components', l.item.StockItemId],
      queryFn: ({ signal }: { signal?: AbortSignal }) =>
        getStockItemComponents(l.item.StockItemId, signal),
    })),
  });

  const loading = partQueries.some((q) => q.isLoading);
  const failed = partQueries.some((q) => q.isError);

  return (
    <div className="mx-auto max-w-[210mm] bg-white p-8 text-black print:p-0">
      {/* Screen-only action bar — the only thing this page hides when printed. */}
      <div className="mb-6 flex flex-wrap items-center gap-3 print:hidden">
        <button
          type="button"
          onClick={onBack}
          className="rounded border border-border bg-white px-3 py-1.5 text-sm font-medium text-darkGrey hover:bg-bg"
        >
          Back to stock
        </button>
        <button
          type="button"
          onClick={() => window.print()}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded bg-orange px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
        >
          <Printer className="h-4 w-4" aria-hidden /> Print or save as PDF
        </button>
        <span className="text-xs text-darkGrey/70">
          Choose <strong>Save as PDF</strong> as the destination to get a file.
        </span>
      </div>

      {failed && (
        <p role="alert" className="mb-4 rounded border border-red/30 bg-red/10 px-3 py-2 text-sm text-red print:hidden">
          Some parts lists could not be loaded, so those items show without their parts. Go back
          and try again rather than sending an incomplete request.
        </p>
      )}

      {/* ⚠ print-color-adjust, or browsers drop the navy and the document
          arrives as plain black text with no masthead. */}
      <header
        className="mb-6 rounded-t bg-navy px-5 py-4 text-white"
        style={{ printColorAdjust: 'exact', WebkitPrintColorAdjust: 'exact' }}
      >
        <h1 className="font-heading text-xl font-bold">Request for Quote</h1>
        <p className="mt-0.5 text-sm opacity-90">Onsite Rental Group</p>
      </header>

      <dl className="mb-6 grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
        {supplier && (
          <>
            <dt className="font-semibold text-darkGrey">To</dt>
            <dd>{supplier}</dd>
          </>
        )}
        {reference && (
          <>
            <dt className="font-semibold text-darkGrey">Our reference</dt>
            <dd>{reference}</dd>
          </>
        )}
        <dt className="font-semibold text-darkGrey">Date</dt>
        <dd>{today()}</dd>
      </dl>

      <p className="mb-6 text-sm">
        Please quote unit prices <strong>excluding GST</strong>, with lead time and warranty
        terms, for the following. This is a request for a quotation and is not an order.
      </p>

      <ol className="space-y-5">
        {lines.map((l, i) => {
          const parts: StockComponent[] = partQueries[i]?.data?.Components ?? [];
          return (
            <li key={l.item.StockItemId} className="break-inside-avoid border-b border-border pb-4 last:border-b-0">
              <div className="flex items-baseline justify-between gap-4">
                <h2 className="font-heading text-base font-bold text-navy">
                  {i + 1}. {l.item.Name}
                </h2>
                <p className="shrink-0 text-sm">
                  <span className="font-semibold">Quantity required:</span>{' '}
                  {l.qty === '' ? 1 : l.qty}
                </p>
              </div>

              {supplierFields(l.item).length > 0 && (
                <dl className="mt-1 grid grid-cols-[auto,1fr] gap-x-3 text-sm">
                  {supplierFields(l.item).map((f) => (
                    <div key={f.label} className="contents">
                      <dt className="font-semibold text-darkGrey">{f.label}</dt>
                      <dd>{f.value}</dd>
                    </div>
                  ))}
                </dl>
              )}

              {l.item.Description && (
                <p className="mt-2 whitespace-pre-line text-sm">{l.item.Description}</p>
              )}

              {parts.length > 0 && (
                <div className="mt-3">
                  <p className="text-sm font-semibold text-darkGrey">Made up of</p>
                  <table className="mt-1 w-full border-collapse text-sm">
                    <thead>
                      <tr className="border-b border-border text-left">
                        <th className="w-16 py-1 font-semibold">Qty</th>
                        <th className="py-1 font-semibold">Part</th>
                        <th className="w-44 py-1 font-semibold">Part number</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parts.map((p) => (
                        <tr key={p.StockItemComponentId} className="border-b border-border/50 last:border-b-0">
                          <td className="py-1">{p.Qty}</td>
                          <td className="py-1">{p.Description ?? '—'}</td>
                          <td className="py-1">{p.SupplierPartNumber ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {!l.item.Description && !l.item.SupplierPartNumber && parts.length === 0 && (
                <p className="mt-2 text-sm italic">
                  Specification to be confirmed — please contact us before quoting.
                </p>
              )}
            </li>
          );
        })}
      </ol>

      <p className="mt-8 border-t border-border pt-3 text-xs text-darkGrey/70">
        Generated from Oscar on {today()}.
      </p>
    </div>
  );
}
